import { useEffect, useRef, useState } from "react";
import { API } from "../lib/api";

const OSM_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * Loads Leaflet (bundled, not from a CDN) and the brand style sheet exactly
 * once. Returns the `L` module. Code-splits via dynamic import so this
 * ~40 KB (gzip) library only ships with the GIS mode, never on first paint.
 */
let _leafletPromise = null;
function ensureLeaflet() {
  if (!_leafletPromise) {
    _leafletPromise = (async () => {
      const [{ default: L }] = await Promise.all([
        import("leaflet"),
        import("leaflet/dist/leaflet.css"),
      ]);

      // Brand overlay: dark canvas, muted OSM tiles, gold attribution/zoom chrome.
      if (
        typeof document !== "undefined" &&
        !document.getElementById("fom-leaflet-brand-css")
      ) {
        const style = document.createElement("style");
        style.id = "fom-leaflet-brand-css";
        style.textContent = `
          .leaflet-container.fom-leaflet { background: #0A0A0A; outline: none; }
          .fom-leaflet .fom-osm-tile .leaflet-tile {
            filter: invert(1) hue-rotate(190deg) brightness(0.9) contrast(0.9) saturate(0.6);
          }
          .fom-leaflet .leaflet-control-attribution {
            background: rgba(10,10,10,0.7); color: rgba(253,251,247,0.55);
            font-size: 10px; padding: 2px 6px;
            border: 1px solid rgba(212,175,55,0.2);
          }
          .fom-leaflet .leaflet-control-attribution a { color: #D4AF37; }
          .fom-leaflet .leaflet-bar a {
            background: #0A0A0A; color: #D4AF37;
            border-color: rgba(212,175,55,0.3);
          }
          .fom-leaflet .leaflet-bar a:hover { background: #121212; }
          .fom-leaflet .leaflet-popup-content-wrapper {
            background: #FDFBF7; border-radius: 2px; border: 1px solid #D4AF37;
          }
          .fom-leaflet .leaflet-popup-tip {
            background: #FDFBF7; border: 1px solid #D4AF37;
          }
        `;
        document.head.appendChild(style);
      }
      return L;
    })();
  }
  return _leafletPromise;
}

/**
 * Dark-themed Leaflet map with real GeoJSON overlay for Fulton · DeKalb ·
 * College Park. Bundled Leaflet (no CDN) → works even behind strict CSPs.
 *
 * Props:
 *   active        – when false the map is destroyed (saves memory in Stylized mode).
 *   highlightCommunity – optional "Fulton" | "DeKalb" | "College Park" — zooms + flashes that polygon.
 */
export default function LeafletFOMMap({ active = true, highlightCommunity = null }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const [status, setStatus] = useState("loading"); // loading | ready | error

  // Mount / unmount
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let localMap = null;

    (async () => {
      try {
        const L = await ensureLeaflet();
        if (cancelled || !containerRef.current) return;

        localMap = L.map(containerRef.current, {
          zoomControl: true,
          scrollWheelZoom: false,
          attributionControl: true,
          preferCanvas: true,
        }).setView([33.78, -84.33], 10);
        mapRef.current = localMap;
        containerRef.current.classList.add("fom-leaflet");

        L.tileLayer(OSM_URL, {
          attribution: OSM_ATTR,
          maxZoom: 19,
          className: "fom-osm-tile",
        }).addTo(localMap);

        // Fetch GeoJSON from the backend (absolute URL — production-safe)
        const resp = await fetch(`${API}/credit-union/fom/geojson`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const gj = await resp.json();

        const styleFor = (feature, highlighted = false) => {
          const color = feature.properties?.color || "#D4AF37";
          return {
            color,
            weight: highlighted ? 3 : 2,
            fillColor: color,
            fillOpacity: highlighted ? 0.5 : 0.3,
            opacity: 1,
          };
        };

        const layer = L.geoJSON(gj, {
          style: (f) => styleFor(f),
          onEachFeature: (feature, lyr) => {
            const p = feature.properties || {};
            layersRef.current[p.community] = lyr;

            lyr.bindPopup(
              `<div style="font-family:'Cormorant Garamond',Georgia,serif;color:#0A0A0A;min-width:180px">
                <div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#8B6914;margin-bottom:4px">Proposed FOM</div>
                <div style="font-size:20px;font-weight:700;line-height:1.1">${p.label || "Community"}</div>
                <div style="font-size:12px;color:#555;margin-top:4px">${p.name || ""}</div>
                <div style="margin-top:8px;font-family:Helvetica,Arial,sans-serif;font-size:11px;color:#444">
                  Boundary · US Census TIGER (${new Date().getFullYear()})
                </div>
              </div>`,
            );
            lyr.on("mouseover", () => lyr.setStyle(styleFor(feature, true)));
            lyr.on("mouseout", () => lyr.setStyle(styleFor(feature, false)));
          },
        }).addTo(localMap);

        localMap.fitBounds(layer.getBounds().pad(0.05));

        if (!cancelled) setStatus("ready");
      } catch (err) {
        console.error("[fom-leaflet] load failed", err);
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (mapRef.current) mapRef.current.remove();
      } catch (_e) {
        /* ignore */
      }
      mapRef.current = null;
      layersRef.current = {};
    };
  }, [active]);

  // React to highlightCommunity changes without rebuilding the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const target = highlightCommunity ? layersRef.current[highlightCommunity] : null;

    Object.entries(layersRef.current).forEach(([community, lyr]) => {
      const color =
        community === "Fulton"
          ? "#D4AF37"
          : community === "DeKalb"
          ? "#2EA44F"
          : "#E32636";
      const highlighted = community === highlightCommunity;
      lyr.setStyle({
        color,
        weight: highlighted ? 3 : 2,
        fillColor: color,
        fillOpacity: highlighted ? 0.55 : 0.3,
        opacity: 1,
      });
    });

    if (target) {
      try {
        map.flyToBounds(target.getBounds().pad(0.1), { duration: 0.8 });
      } catch (_e) {
        /* no-op */
      }
    }
  }, [highlightCommunity]);

  return (
    <div className="relative" data-testid="fom-gis-map">
      <div
        ref={containerRef}
        className="w-full"
        style={{ height: "520px", background: "#0A0A0A" }}
      />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none">
          <div className="text-xs uppercase tracking-[0.3em] text-bdu-cream/60 animate-pulse">
            Loading map tiles…
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70">
          <div className="text-xs uppercase tracking-widest text-bdu-red">
            Failed to load the GIS overlay. Try again in a moment.
          </div>
        </div>
      )}
    </div>
  );
}
